import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ActionCompletion, Actions, ofActionCompleted, Select, Store } from '@ngxs/store';
import { Observable, tap } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NgxsBaseComponent } from '../ngxs-base.component';
import {
  EmailAcknowledgementState,
  EmailAcknowledgementStateModel,
} from '../../../states/email-acknowledgement/email-acknowledgement.state';
import {
  GetEmailAcknowledgeStatus,
  SubmitEmailAcknowledge,
} from '../../../states/email-acknowledgement/email-acknowledgement.state-actions';
import { environment } from '../../../environments/environment';
import { isHttpUrl } from '../../../utils/http-url-validator';

@Component({
  selector: 'grabbill-client-acknowledge-email',
  templateUrl: './acknowledge-email.component.html',
  styleUrls: ['./acknowledge-email.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AcknowledgeEmailComponent extends NgxsBaseComponent {
  @Select(EmailAcknowledgementState.current)
  state$!: Observable<EmailAcknowledgementStateModel>;

  isLoading = true;
  isInvalid = false;

  token?: string;

  constructor(
    private actions$: Actions,
    private cd: ChangeDetectorRef,
    private route: ActivatedRoute,
    protected override store: Store,
    protected override messageService: NzMessageService
  ) {
    super(store, messageService);
  }

  get environment() {
    return environment.config;
  }

  canRedirect(url?: string): boolean {
    return isHttpUrl(url);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.token = this.route.snapshot.queryParams['token'];

    this.autoUnsubscribe(
      this.actions$.pipe(
        ofActionCompleted(GetEmailAcknowledgeStatus, SubmitEmailAcknowledge),
        tap((data: ActionCompletion) => {
          this.isLoading = false;
          this.isInvalid = !!data.result.error;
          this.cd.markForCheck();
        })
      )
    );

    // EXPIRED/DELIVERY_FAILED with a redirectUrl hard-navigate away immediately (Custom Expiry
    // URL, or - for a Custom Landing Page Type - the Fail Landing URL). ALREADY_ACKNOWLEDGED with
    // a redirectUrl covers a fresh, successful acknowledgement for a Custom Landing Page Type -
    // see EmailAcknowledgementController, which reuses this same status for "just acknowledged".
    // For every other Type, no status ever carries a redirectUrl here, so this never fires - the
    // recipient just sees our own normal confirmation, same as before this feature existed.
    this.autoUnsubscribe(
      this.state$.pipe(
        tap((state) => {
          // isHttpUrl also guards against values saved before validation existed - a
          // non-http(s) URL just falls through to our own message instead of executing.
          if ((state.status === 'EXPIRED' || state.status === 'DELIVERY_FAILED' || state.status === 'ALREADY_ACKNOWLEDGED') && isHttpUrl(state.redirectUrl)) {
            window.location.href = state.redirectUrl!;
          } else if (state.status === 'FRESH' && state.hasCustomLandingPage && this.token) {
            // One-click flow: no confirmation button is shown at all (see the template) - submit
            // immediately with no name/remarks, then the redirectUrl branch above takes over.
            this.store.dispatch(new SubmitEmailAcknowledge(this.token));
          }
        })
      )
    );

    if (this.token) {
      this.store.dispatch(new GetEmailAcknowledgeStatus(this.token));
    } else {
      this.isLoading = false;
      this.isInvalid = true;
    }
  }

  submitForm(): void {
    if (this.token) {
      this.isLoading = true;
      this.cd.markForCheck();
      this.store.dispatch(new SubmitEmailAcknowledge(this.token));
    }
  }
}
